/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2020 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    customSettings: getSettingsCollection(state).customSettings,
});

type Props = WidgetProps & HasProduct & ReturnType<typeof mapStateToProps>;

export interface ProductDetailsPartNumberStyles {
    labelText?: TypographyProps;
    valueText?: TypographyProps;
}

/**
  * 
   color: #3A3A3A;
   letter-spacing: 0;
   line-height: 22px;
  */
export const partNumberStyles: ProductDetailsPartNumberStyles = {
    labelText: {
        size: 14,
        lineHeight: "22px",
        weight: "bold",
        css: css`
            margin-right: 2px;
        `,
    },
    valueText: {
        size: 14,
        lineHeight: "22px",
        css: css``,
    },
};

const styles = partNumberStyles;

const ProductDetailsPartNumber: React.FC<Props> = ({ product, customSettings }) => {
    const showModelNumber = (customSettings as any).showModelNumber.toLowerCase() === "true";
    return (
        <>
            {product.properties.specNumber && (
                <Typography {...styles.valueText} as="p">
                    <Typography {...styles.labelText}>{siteMessage("Custom_Spec_#", "Spec #:")}</Typography>
                    {product.properties.specNumber}
                </Typography>
            )}
            {product.manufacturerItem && (
                <Typography {...styles.valueText} as="p">
                    <Typography {...styles.labelText}>{siteMessage("Custom_MFR_#", "MFR #:")}</Typography>
                    {product.manufacturerItem}
                </Typography>
            )}
            {product.productNumber && (
                <Typography {...styles.valueText} as="p" data-test-selector="ProductDetailsPartNumber">
                    <Typography {...styles.labelText}>{siteMessage("Custom_Part_#", "EIS Part #:")}</Typography>
                    {product.productNumber}
                </Typography>
            )}
            {product.detail?.productCode && (
                <Typography {...styles.valueText} as="p">
                    <Typography {...styles.labelText}>{siteMessage("Custom_Series_#", "Series #:")}</Typography>
                    {product.detail?.productCode}
                </Typography>
            )}
            {product.detail?.upcCode && (
                <Typography {...styles.valueText} as="p">
                    <Typography {...styles.labelText}>{siteMessage("Custom_UPC_#", "UPC:")}</Typography>
                    {product.detail.upcCode}
                </Typography>
            )}
            {product.detail?.modelNumber && showModelNumber && (
                <Typography {...styles.valueText} as="p">
                    <Typography {...styles.labelText}>{siteMessage("Custom_Model_#", "Model #:")}</Typography>
                    {product.detail.modelNumber}
                </Typography>
            )}
        </>
    );
};

const widgetModule: WidgetModule = {
    component: withProduct(connect(mapStateToProps)(ProductDetailsPartNumber)),
    definition: {
        displayName: "Part Number",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
